import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ErroApi } from '../api';
import { Pendencias } from '../Pendencias';
import {
  descartarGranulometriaLeitura, descartarGranulometriaUmidade,
  gravarGranulometriaCabecalho, gravarGranulometriaPeneiras,
  gravarGranulometriaSedimentacao, gravarGranulometriaUmidades,
  verGranulometria,
} from '../api/granulometria';
import type { EnsaioGranulometria } from '../api/granulometria';
import {
  AncoraDePendencias, BlocoDaFicha, CabecalhoDaFicha, EscolhaDeBalanca,
  EtapasDaFicha, FaixaDaFicha, JanelaDeDescarte, LaudoDaFicha, RodapeDeEtapas,
  fichaCongelada, useEquipamentos, useEtapa, useFicha,
} from '../Ficha';
import type { Etapa, Indicador } from '../Ficha';
import { numero, paraNumero, paraCampo, plural, rotulo } from '../formato';
import { FotosDoRegistro } from '../FotosDoRegistro';
import { Achado, Plano, Serie } from '../Grafico';
import {
  Aviso, Campo, CampoNumerico, Carregando, ErroDaApi, Escolha,
  EscolhaPorCodigo, RespostaDaAcao, Selo, TabelaDeLancamento, comoCodigo,
} from '../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../ui';
import { useSessao } from '../sessao';
import {
  LembreteDaEstufa, SecaDaLinha, WDaLinha, naEstufaDaFicha, useEstufaDoEnsaio,
} from './NaEstufa';
import type { Estufa } from './NaEstufa';
import { AvisoDaRepeticaoAberta } from './Triviais';
import { useLinhasDaRepeticao } from './repeticao/Repeticao';
import { FracoesLadoALado, RepeticaoGranulometria } from './repeticao/RepeticaoGranulometria';
import { TentativasAnteriores } from './repeticao/TentativasAnteriores';
import { TrilhaDoEnsaio } from './repeticao/TrilhaDoEnsaio';

/**
 * Lançar uma granulometria por peneiramento e sedimentação — da balança
 * ao PDF (ABNT NBR 7181:2025).
 *
 * ─── No Carbon (onda 248) ──────────────────────────────────────────────
 *
 * A espinha de `Ficha.tsx`: cabeçalho, faixa (as frações, Cu e Cc sempre à
 * vista) e cinco etapas, na ordem da bancada:
 *
 *   1. amostra — Mt, Mu, preparação
 *   2. umidade higroscópica — as cápsulas (ou o valor transcrito)
 *   3. peneiramento — o grosso, até a 2,0 mm, e o fino, do passado nela
 *   4. sedimentação — o densímetro e as leituras de 30 s a 24 h (0221)
 *   5. curva e laudo — as frações, a curva, as fotos e o PDF
 *
 * A CURVA GRANULOMÉTRICA aparece na tela desde o peneiramento — a mesma do
 * laudo (`granulometria-documento.service`): diâmetro em log, porcentagem
 * que passa, os pontos ligados em ordem e as divisórias da NBR 6502. Nada
 * é ajustado aqui; d10, d30 e d60 marcados são os que o banco calculou.
 *
 * ─── As três balanças (RN-215, 0222) ───────────────────────────────────
 *
 * Três baterias de pesagem, uma balança cada — escolhida UMA vez por
 * bateria, como nas outras fichas:
 *
 *   · a da amostra total (Mt) e do peneiramento grosso — capacidade;
 *   · a da amostra parcial (Mu) e do peneiramento fino — resolução;
 *   · a das cápsulas da umidade higroscópica.
 *
 * ─── O que a tela não pede ─────────────────────────────────────────────
 *
 * A tara da cápsula: vem da aferição vigente na data do ensaio. E o
 * percentual que passa: é conta, e conta mora no banco (0048). Aqui só
 * entra massa retida.
 *
 * ─── Por que a série de peneiras vem do servidor ───────────────────────
 *
 * Porque a NBR 7181:2025 permite peneiras intermediárias (nota de 4.5.2)
 * e a Testesolo usa várias. A lista é tabela (`peneira_serie`), e a tela
 * só a exibe — acrescentar uma peneira é acrescentar uma linha, não
 * publicar uma versão da interface.
 */

const CAT_CAPSULA = ['capsula'];
const CAT_PENEIRA = ['peneira'];
const CAT_DENSIMETRO = ['densimetro'];

/** As casas que a ficha já usava — as do laudo (0221). */
export const CASAS = { fracao: 2, d: 4, coef: 2, w: 3, massa: 2 } as const;

const num = (v: unknown, casas: number) => numero(v, casas);

/** Fração decimal em percentual, para a tela. */
export const pct = (v: unknown, casas: number = CASAS.fracao) =>
  (v == null || v === '' ? '—' : numero(Number(v) * 100, casas));
const pctOuNulo = (v: unknown, casas: number = CASAS.fracao) =>
  (v == null || v === '' ? null : numero(Number(v) * 100, casas));

/** As divisórias das frações (ABNT NBR 6502), as mesmas do laudo. */
const FRONTEIRAS: { d: number; nome: string }[] = [
  { d: 2, nome: 'pedregulho' },
  { d: 0.6, nome: 'areia grossa' },
  { d: 0.2, nome: 'areia média' },
  { d: 0.06, nome: 'areia fina' },
  { d: 0.002, nome: 'silte' },
];

export function Granulometria() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioGranulometria | null>(null);
  const equip = useEquipamentos();
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const ficha = useFicha(id, 'NBR 7181', versao);
  /* As cápsulas na estufa e o carimbo das pesagens (onda 294, RN-241). */
  const estufa = useEstufaDoEnsaio(id, versao);
  /* R2 (onda 323): com a repetição aberta, as etapas de bancada listam e
     lançam as linhas DELA. */
  const rep = useLinhasDaRepeticao<EnsaioGranulometria>(
    id, 'granulometria', d?.repeticaoAberta, versao);
  const { pode } = useSessao();
  /* A Interpretação (onda 323) existe só para quem interpreta. */
  const interpreta = pode('ensaio.interpretar');
  const total = interpreta ? 6 : 5;

  const carregar = useCallback(async () => {
    try { setD(await verGranulometria(id)); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<EnsaioGranulometria>,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      setD(await chamada());
      setOk('Gravado.');
      setVersao((v) => v + 1);
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, []);

  const opcoes = (cats: string[]): Opcao[] => equip
    .filter((e) => cats.includes(e.categoria))
    /* `detalhe` alimenta o filtro do `EscolhaPorCodigo`: quem digita
       «118» acha CAP-0118. */
    .map((e) => ({ valor: e.id, texto: e.codigo_patrimonial,
      detalhe: e.nome }));

  const campoErro = (c: string) =>
    erro instanceof ErroApi ? erro.campo(c) : undefined;

  const c = d?.cabecalho;
  /* O que as etapas de bancada mostram: a repetição aberta, se houver (R2).
     A faixa, as pendências e o laudo continuam com a vigente (D5). */
  const repAberta = d?.repeticaoAberta != null;
  const daBancada: EnsaioGranulometria | null = d && repAberta && rep.atual
    ? { ...d, cabecalho: rep.atual.cabecalho ?? d.cabecalho,
        peneiras: rep.atual.peneiras ?? [], umidades: rep.atual.umidades ?? [],
        sedimentacao: rep.atual.sedimentacao ?? [],
        balanca: rep.atual.balanca ?? d.balanca,
        balancaFina: rep.atual.balancaFina ?? d.balancaFina,
        balancaUmidade: rep.atual.balancaUmidade ?? d.balancaUmidade }
    : d;
  const cb = daBancada?.cabecalho;
  const peneiras = daBancada?.peneiras ?? [];
  const grossas = peneiras.filter((p) => p.fase === 'grossa');
  const finas = peneiras.filter((p) => p.fase === 'fina');
  const capsulasValidas = (daBancada?.umidades ?? []).filter((u) => !u.descartada).length;
  const leiturasOk = (daBancada?.sedimentacao ?? [])
    .filter((x) => !x.descartada && x.situacao === 'ok').length;
  const temAmostra = c?.massa_total_umida != null;
  const temUmidade = cb?.umidade_origem === 'capsulas'
    || cb?.umidade_origem === 'informada';
  const temPeneiras = grossas.length > 0;
  const comSed = !!c?.com_sedimentacao;
  const [etapa, irPara] = useEtapa(total,
    !temAmostra ? 1 : !temUmidade ? 2 : !temPeneiras ? 3
      : comSed && !leiturasOk ? 4 : total);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d || !daBancada) return <Carregando texto="Abrindo o ensaio" />;
  const congelado = fichaCongelada(ficha?.status);
  const naTentativa = repAberta ? ` · tentativa ${d.repeticaoAberta}` : '';
  const lendoRep = repAberta && rep.carregando;

  const faltaBalancaAmostra = (temAmostra || grossas.length > 0) && !daBancada.balanca;
  const faltaBalancaFina = (c?.massa_parcial_umida != null || finas.length > 0)
    && !daBancada.balancaFina;
  const faltaBalancaUmidade = capsulasValidas > 0 && !daBancada.balancaUmidade;

  const etapas: Etapa[] = [
    { nome: 'Amostra', feita: temAmostra && !faltaBalancaAmostra,
      falta: faltaBalancaAmostra,
      resumo: temAmostra
        ? `Mt ${num(c.massa_total_umida, CASAS.massa)} g`
          + (c?.massa_parcial_umida != null
            ? ` · Mu ${num(c.massa_parcial_umida, CASAS.massa)} g` : '')
        : 'a gravar' },
    { nome: 'Umidade', feita: temUmidade && !faltaBalancaUmidade,
      falta: faltaBalancaUmidade,
      resumo: (cb?.umidade_origem === 'capsulas'
        ? plural(capsulasValidas, 'cápsula', 'cápsulas')
        : cb?.umidade_origem === 'informada' ? 'transcrita' : 'a lançar') + naTentativa },
    { nome: 'Peneiramento', feita: temPeneiras && !faltaBalancaFina,
      falta: faltaBalancaFina || (grossas.length > 0 && !daBancada.balanca),
      resumo: `${grossas.length} grossa(s) · ${finas.length} fina(s)${naTentativa}` },
    { nome: 'Sedimentação', feita: !comSed || leiturasOk > 0,
      resumo: (comSed ? plural(leiturasOk, 'leitura', 'leituras')
        : 'só peneiramento') + (comSed ? naTentativa : '') },
    ...(interpreta ? [{
      nome: 'Interpretação', feita: c?.fracao_areia_media != null && !repAberta,
      resumo: repAberta ? `repetindo · tentativa ${d.repeticaoAberta}`
        : c?.d10 != null ? `D₁₀ ${num(c.d10, CASAS.d)} mm` : 'depois das peneiras',
    }] : []),
    { nome: 'Curva e laudo', feita: ficha?.status === 'aprovado' },
  ];

  const porque = 'A curva ainda não chega lá.';
  const indicadores: Indicador[] = [
    { rotulo: 'Pedregulho', unidade: '%', valor: pctOuNulo(c?.fracao_pedregulho),
      porque, nota: '> 2,0 mm',
      como: 'Retido acima de 2,0 mm, pelas divisórias da ABNT NBR 6502.' },
    { rotulo: 'Areia grossa', unidade: '%', valor: pctOuNulo(c?.fracao_areia_grossa),
      porque, nota: '0,6 – 2,0 mm' },
    { rotulo: 'Areia média', unidade: '%', valor: pctOuNulo(c?.fracao_areia_media),
      porque, nota: '0,2 – 0,6 mm' },
    ...(comSed
      ? [
        { rotulo: 'Areia fina', unidade: '%', valor: pctOuNulo(c?.fracao_areia_fina),
          porque, nota: '0,06 – 0,2 mm' },
        { rotulo: 'Silte', unidade: '%', valor: pctOuNulo(c?.fracao_silte),
          porque: 'A sedimentação não alcançou 0,002 mm.', nota: '0,002 – 0,06 mm',
          como: 'Da sedimentação (NBR 7181:2025, 4.3 e 5.3–5.4).' },
        { rotulo: 'Argila', unidade: '%', valor: pctOuNulo(c?.fracao_argila),
          porque: 'A sedimentação não alcançou 0,002 mm.', nota: '< 0,002 mm',
          como: 'O que passa em 0,002 mm. Sem leitura até lá, não se estima.' },
      ] as Indicador[]
      : [
        { rotulo: `Passa na ${num(c?.abertura_minima, 3)} mm`, unidade: '%',
          valor: pctOuNulo(c?.finos_abaixo_da_ultima_peneira), porque,
          nota: 'silte e argila não determinados',
          como: 'Só peneiramento: o último número verdadeiro da curva é o que passa na peneira mais fina.' },
      ] as Indicador[]),
    { rotulo: 'D₁₀', unidade: 'mm',
      valor: c?.d10 != null ? num(c.d10, CASAS.d) : null,
      porque: 'A curva não alcança 10 % que passa.' },
    { rotulo: 'Cu', valor: c?.coeficiente_uniformidade != null
      ? num(c.coeficiente_uniformidade, CASAS.coef) : null,
      porque: 'Sem d10 não há Cu.', como: <>C<sub>u</sub> = D<sub>60</sub> / D<sub>10</sub>.</> },
    { rotulo: 'Cc', valor: c?.coeficiente_curvatura != null
      ? num(c.coeficiente_curvatura, CASAS.coef) : null,
      porque: 'Sem d10 não há Cc.',
      como: <>C<sub>c</sub> = D<sub>30</sub>² / (D<sub>10</sub> · D<sub>60</sub>).</> },
  ];

  return (
    <div className="tela granulometria ficha-carbon">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'GRAN'}
        titulo="Granulometria" ficha={ficha} normaDaTela="ABNT NBR 7181"
        tentativaAberta={d?.repeticaoAberta} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />

      {/* O componente é o mesmo das outras fichas (0090/0091): um
        * segundo desenho da mesma coisa é como duas telas passam a dizer
        * coisas diferentes sobre a mesma regra. */}
      <AncoraDePendencias>
        <Pendencias itens={d.pendencias} id={id}
          detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
      </AncoraDePendencias>
      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(ficha?.status).toLowerCase()} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}

      {repAberta && etapa <= 4 && (
        <AvisoDaRepeticaoAberta numero={d.repeticaoAberta!} interpreta={interpreta} />
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <Amostra d={daBancada} id={id} gravar={gravar} salvando={salvando}
          campoErro={campoErro} equipamentos={equip} />
      )}

      {etapa >= 2 && etapa <= 4 && lendoRep && (
        <Carregando texto="Lendo as linhas da repetição" />
      )}

      {etapa === 2 && !lendoRep && (
        <Capsulas key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
          d={daBancada} id={id} gravar={gravar} salvando={salvando}
          campoErro={campoErro} equipamentos={equip}
          opcoesCapsula={opcoes(CAT_CAPSULA)}
          estufa={estufa} congelado={congelado}
          recarregar={async () => { await carregar(); setVersao((v) => v + 1); }} />
      )}

      {etapa === 3 && !lendoRep && (
        <>
          <Peneiras key={repAberta ? `g-rep-${d.repeticaoAberta}` : 'g-vigente'}
            d={daBancada} id={id} gravar={gravar} salvando={salvando}
            campoErro={campoErro} equipamentos={equip} fase="grossa"
            opcoesPeneira={opcoes(CAT_PENEIRA)} />
          <Peneiras key={repAberta ? `f-rep-${d.repeticaoAberta}` : 'f-vigente'}
            d={daBancada} id={id} gravar={gravar} salvando={salvando}
            campoErro={campoErro} equipamentos={equip} fase="fina"
            opcoesPeneira={opcoes(CAT_PENEIRA)} />
          <CurvaGranulometrica d={daBancada} />
        </>
      )}

      {etapa === 4 && !lendoRep && (
        <>
          <Sedimentacao key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
            d={daBancada} id={id} gravar={gravar} salvando={salvando}
            opcoesDensimetro={opcoes(CAT_DENSIMETRO)} />
          {comSed && <CurvaGranulometrica d={daBancada} />}
        </>
      )}

      {interpreta && etapa === 5 && (
        <>
          {/* A repetição na Interpretação (onda 323, RN-260, D7: as curvas e as frações). */}
          <RepeticaoGranulometria id={id} congelado={congelado}
            opcoesPeneira={opcoes(CAT_PENEIRA)}
            aoMudar={async () => { await carregar(); setVersao((v) => v + 1); }}>
            <Resultado c={c} />
            <CurvaGranulometrica d={d} />
          </RepeticaoGranulometria>
          <TentativasAnteriores ensaioId={id} ficha="granulometria" versao={versao}
            render={(r: any) => <FracoesLadoALado inicial={r} atual={null} />} />
          <TrilhaDoEnsaio ensaioId={id} versao={versao} />
        </>
      )}

      {etapa === total && (
        <>
          <Resultado c={c} />
          <CurvaGranulometrica d={d} />
          <FotosDoRegistro
            id={id}
            titulo="Registro fotográfico"
            dica={'O material, o retido, o que a fotografia tiver a dizer sobre a amostra.'}
            roteiro={[]}
          />
          <LaudoDaFicha id={id} />
        </>
      )}
      </fieldset>

      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
    </div>
  );
}

interface GravarFn {
  (marca: string, chamada: () => Promise<EnsaioGranulometria>):
  Promise<boolean>;
}

interface Comum {
  d: EnsaioGranulometria; id: string; gravar: GravarFn;
  salvando: string | null;
}

// ── 1 · a amostra ─────────────────────────────────────────────────────

function Amostra({ d, id, gravar, salvando, campoErro, equipamentos }: Comum & {
  campoErro(x: string): string | undefined;
  equipamentos: ReturnType<typeof useEquipamentos>;
}) {
  const c = d.cabecalho;
  const [preparacao, setPreparacao] = useState<string | null>(null);
  const [mt, setMt] = useState('');
  const [mu, setMu] = useState('');
  const [wInf, setWInf] = useState('');
  const [obs, setObs] = useState('');
  const [balanca, setBalanca] = useState(d.balanca?.id ?? '');
  const [balancaFina, setBalancaFina] = useState(d.balancaFina?.id ?? '');
  useEffect(() => { setBalanca(d.balanca?.id ?? ''); }, [d.balanca?.id]);
  useEffect(() => { setBalancaFina(d.balancaFina?.id ?? ''); }, [d.balancaFina?.id]);

  useEffect(() => {
    setPreparacao(c?.preparacao ?? 'sem_secagem_previa');
    setMt(c?.massa_total_umida != null ? paraCampo(c.massa_total_umida) : '');
    setMu(c?.massa_parcial_umida != null ? paraCampo(c.massa_parcial_umida) : '');
    /*
     * Percentual dos dois lados. A equação 5.1 da norma quer w decimal,
     * mas a conversão mora no banco, uma vez — e nunca aqui, senão a
     * tela vira o segundo lugar onde a unidade pode se perder.
     */
    setWInf(c?.umidade_higroscopica_informada != null
      ? paraCampo(c.umidade_higroscopica_informada) : '');
    setObs(c?.observacoes ?? '');
  }, [c?.preparacao, c?.massa_total_umida, c?.massa_parcial_umida,
    c?.umidade_higroscopica_informada, c?.observacoes]);

  const falta = paraNumero(mt) == null ? 'Falta Mt — a amostra total seca ao ar.'
    : !balanca ? 'Falta escolher a balança da amostra total.'
      : paraNumero(mu) != null && !balancaFina
        ? 'Falta escolher a balança da amostra parcial.'
        : null;

  const enviar = async () => {
    const w = paraNumero(wInf);
    await gravar('cabecalho', () => gravarGranulometriaCabecalho(id, {
      preparacao,
      massaTotalUmida: paraNumero(mt) ?? undefined,
      massaParcialUmida: paraNumero(mu) ?? undefined,
      umidadeHigroscopicaInformada: w ?? undefined,
      observacoes: obs || undefined,
      ...(balanca ? { balancaEquipamentoId: balanca } : {}),
      ...(balancaFina ? { balancaFinaEquipamentoId: balancaFina } : {}),
    }));
  };

  return (
    <BlocoDaFicha titulo="Amostra"
      sub="Mt é pesada na balança de capacidade, a mesma do peneiramento grosso; Mu, na de resolução, a mesma do peneiramento fino (NBR 7181:2025, 4.2 e 4.5).">
      <div className="fe-miolo fe-campos">
        <Escolha rotulo="Preparação" valor={preparacao}
          aoEscolher={setPreparacao} limpavel={false}
          opcoes={[
            { valor: 'sem_secagem_previa', texto: 'sem secagem prévia' },
            { valor: 'com_secagem_previa', texto: 'com secagem prévia' },
          ]} />
        <Campo rotulo="Mt — amostra total seca ao ar (g)" inputMode="decimal"
          value={mt} onChange={(e) => setMt(e.target.value)}
          erro={campoErro('massaTotalUmida')} />
        <EscolhaDeBalanca rotulo="Balança da amostra total e do grosso"
          equipamentos={equipamentos} valor={balanca} aoEscolher={setBalanca}
          erro={campoErro('balancaEquipamentoId')} />
        <Campo rotulo="Mu — massa úmida do peneiramento fino (g)"
          inputMode="decimal" value={mu}
          onChange={(e) => setMu(e.target.value)}
          erro={campoErro('massaParcialUmida')} />
        <EscolhaDeBalanca rotulo="Balança da amostra parcial e do fino"
          equipamentos={equipamentos} valor={balancaFina}
          aoEscolher={setBalancaFina}
          erro={campoErro('balancaFinaEquipamentoId')} />
        <Campo rotulo="w higroscópica transcrita (%)" inputMode="decimal"
          value={wInf} onChange={(e) => setWInf(e.target.value)}
          erro={campoErro('umidadeHigroscopicaInformada')}
          dica="Só quando a umidade veio de outro ensaio. Havendo cápsulas
                lançadas, elas mandam." />
        <Campo rotulo="Observações" value={obs}
          onChange={(e) => setObs(e.target.value)} />
      </div>
      <div className="acoes fe-acoes">
        {falta && <p className="o-que-falta">{falta}</p>}
        <button type="button" className="principal"
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? undefined}
          onClick={() => { if (!falta && salvando === null) void enviar(); }}>
          {salvando === 'cabecalho' ? 'Gravando…' : 'Gravar amostra'}
        </button>
      </div>
      {c?.massa_total_seca != null && (
        <div className="fe-miolo">
          <dl className="fe-mosaico">
            <div><dt>w higroscópica</dt>
              <dd>{num(c.umidade_higroscopica, CASAS.w)} %{' '}
                <Selo tom={c.umidade_origem === 'capsulas' ? 'ok' : 'atencao'}>
                  {c.umidade_origem === 'capsulas' ? 'das cápsulas'
                    : c.umidade_origem === 'informada' ? 'transcrita'
                      : 'ausente'}
                </Selo>
              </dd></div>
            <div><dt>Mg — retido na 2,0 mm</dt>
              <dd>{num(c.massa_retida_2mm, CASAS.massa)} g</dd></div>
            <div className="destaque"><dt>Ms(t) — massa total seca</dt>
              <dd>{num(c.massa_total_seca, CASAS.massa)} g</dd></div>
            <div><dt>N — passa na 2,0 mm</dt>
              <dd>{pct(c.percentual_passa_2mm)} %</dd></div>
          </dl>
        </div>
      )}
    </BlocoDaFicha>
  );
}

// ── 2 · a umidade higroscópica ────────────────────────────────────────

interface LinhaCapsula { capsula: string; umido: string; seco: string }
const capsulaVazia = (): LinhaCapsula => ({ capsula: '', umido: '', seco: '' });

function Capsulas({
  d, id, gravar, salvando, campoErro, equipamentos, opcoesCapsula,
  estufa, congelado, recarregar,
}: Comum & {
  campoErro(x: string): string | undefined;
  equipamentos: ReturnType<typeof useEquipamentos>;
  opcoesCapsula: Opcao[];
  estufa: Estufa; congelado: boolean; recarregar(): Promise<void>;
}) {
  const [novas, setNovas] = useState<LinhaCapsula[]>(
    () => (d.umidades ?? []).length ? [capsulaVazia()]
      : [capsulaVazia(), capsulaVazia(), capsulaVazia()]);
  const [balanca, setBalanca] = useState(d.balancaUmidade?.id ?? '');
  useEffect(() => { setBalanca(d.balancaUmidade?.id ?? ''); }, [d.balancaUmidade?.id]);
  const [descartando, setDescartando] = useState<any | null>(null);

  const mexer = (i: number, campo: keyof LinhaCapsula, v: string) =>
    setNovas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));

  const proximaOrdem = useMemo(
    () => (d.umidades ?? []).reduce(
      (m, u) => Math.max(m, Number(u.ordem)), 0) + 1,
    [d.umidades]);

  /* A seca é opcional: sem ela, a cápsula vai para a estufa (RN-241). */
  const prontas = novas.filter((l) => l.capsula && l.umido);
  const mudouBalanca = !!balanca && balanca !== (d.balancaUmidade?.id ?? '');
  const falta = prontas.length && !balanca
    ? 'Falta escolher a balança das cápsulas.'
    : prontas.length || mudouBalanca ? null
      : !novas.some((l) => l.capsula || l.umido || l.seco)
        ? 'Preencha a cápsula e a massa úmida para gravar.'
        : novas.find((l) => !l.capsula) ? 'Falta escolher a cápsula.'
          : 'Falta a massa de cápsula + solo úmido.';
  const naEstufa = (d.umidades ?? []).filter(naEstufaDaFicha).length;

  const enviar = async () => {
    const feito = await gravar('umidades', () => gravarGranulometriaUmidades(id, {
      ...(mudouBalanca || (prontas.length && balanca)
        ? { balancaUmidadeEquipamentoId: balanca } : {}),
      determinacoes: prontas.map((l, i) => ({
        ordem: proximaOrdem + i,
        capsulaEquipamentoId: l.capsula,
        massaCapsulaSoloUmido: paraNumero(l.umido),
        ...(paraNumero(l.seco) != null
          ? { massaCapsulaSoloSeco: paraNumero(l.seco) } : {}),
      })),
    }));
    if (feito) setNovas([capsulaVazia()]);
  };

  const colunas: ColunaDeLancamento[] = [
    { texto: 'Det.' },
    { texto: 'Cápsula' },
    { texto: 'Tara (g)', num: true },
    { texto: 'Cáps. + úmido (g)', num: true },
    { texto: 'Cáps. + seco (g)', num: true },
    { texto: 'w (%)', num: true },
    { texto: '' },
  ];

  /*
   * No celular cada cápsula vira um cartão, com a umidade calculada no
   * cabeçalho dele — o número que se confere antes da próxima (onda 163).
   */
  const lancadas: LinhaDeLancamento[] = (d.umidades ?? []).map((u) => ({
    chave: u.id,
    classe: u.descartada ? 'descartada' : undefined,
    titulo: <>Determinação {u.ordem} · <span className="cod">{u.capsula ?? '—'}</span></>,
    resultado: u.descartada ? '—' : naEstufaDaFicha(u) ? 'na estufa'
      : <span className="n">{num(u.umidade, CASAS.w)} %</span>,
    semCartao: [0, 1, 5],
    celulas: [
      u.ordem,
      <span className="cod">{u.capsula ?? '—'}</span>,
      u.tara == null ? <Selo tom="critico">Sem calibração interna</Selo> : num(u.tara, 2),
      num(u.massa_capsula_solo_umido, 2),
      <SecaDaLinha linha={u} estufa={estufa} congelado={congelado}
        opcoesCapsula={opcoesCapsula} aoMudou={recarregar} />,
      <b className="fe-w"><WDaLinha linha={u} casas={CASAS.w} /></b>,
      u.descartada
        ? <span className="fe-motivo" title={u.motivo_descarte}><Selo tom="neutro">Descartada</Selo></span>
        : (
          <button type="button" className="botao discreto pequeno fe-descartar"
            aria-label={`Descartar a determinação ${u.ordem}`}
            disabled={salvando !== null}
            onClick={() => setDescartando(u)}>
            Descartar
          </button>
        ),
    ],
  }));

  const emBranco: LinhaDeLancamento[] = novas.map((l, i) => ({
    chave: `nova-${i}`,
    classe: 'nova',
    titulo: `Determinação ${proximaOrdem + i} · nova`,
    semCartao: [0, 2, 5],
    celulas: [
      <span className="sub">{proximaOrdem + i}</span>,
      <EscolhaPorCodigo rotulo="" opcoes={opcoesCapsula.map(comoCodigo)}
        valor={l.capsula} aoEscolher={(v) => mexer(i, 'capsula', v)} />,
      <span className="sub">—</span>,
      <CampoNumerico rotulo="Cápsula + solo úmido (g)" rotuloOculto
        value={l.umido} onChange={(ev) => mexer(i, 'umido', ev.target.value)} />,
      <CampoNumerico rotulo="Cápsula + solo seco (g)" rotuloOculto
        value={l.seco} onChange={(ev) => mexer(i, 'seco', ev.target.value)} />,
      <span className="sub">—</span>,
      <button type="button" className="remover" tabIndex={-1}
        aria-label={`Remover a determinação ${proximaOrdem + i}`}
        title="Remover esta linha (ainda não gravada)"
        onClick={() => setNovas((ls) => ls.filter((_, j) => j !== i))}>
        ×
      </button>,
    ],
  }));

  const validas = (d.umidades ?? []).filter((u) => !u.descartada).length;

  return (
    <BlocoDaFicha titulo="Umidade higroscópica"
      selo={validas ? <Selo tom="ok">{plural(validas, 'válida', 'válidas')}</Selo> : undefined}
      sub="A NBR 7181:2025 pede três determinações (4.2.4 a). A tara da cápsula vem da calibração interna vigente na data do ensaio; a balança se escolhe uma vez para as três.">
      <div className="fe-miolo fe-campos">
        <EscolhaDeBalanca rotulo="Balança das cápsulas"
          equipamentos={equipamentos} valor={balanca} aoEscolher={setBalanca}
          erro={campoErro('balancaUmidadeEquipamentoId')} />
        {d.cabecalho?.umidade_higroscopica != null && (
          <p className="sub">
            w higroscópica: <b>{num(d.cabecalho.umidade_higroscopica, CASAS.w)} %</b>
            {d.cabecalho.umidade_origem === 'informada' && ' (transcrita na etapa 1)'}
          </p>
        )}
      </div>
      <div className="fe-tabela">
        <div className="fe-tabela-topo">
          <h3>Determinações <small>{(d.umidades ?? []).length
            ? `${(d.umidades ?? []).length} lançadas` : 'nenhuma lançada ainda'}</small></h3>
          <button type="button" className="botao discreto pequeno"
            onClick={() => setNovas((ls) => [...ls, capsulaVazia()])}>
            + cápsula
          </button>
        </div>
        <TabelaDeLancamento
          rotulo="Determinações de umidade higroscópica — lançadas e novas"
          classe="fe-lanc" colunas={colunas} linhas={[...lancadas, ...emBranco]} />
        {!congelado && <LembreteDaEstufa quantas={naEstufa} />}
        <div className="acoes acoes-fixas fe-acoes">
          {falta
            ? <p className="o-que-falta">{falta}</p>
            : <p className="fe-lembrete">Linha em branco não sobe; cápsula errada se descarta com motivo.</p>}
          <button type="button" className="principal" data-gravar-tabela=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={() => { if (!falta && salvando === null) void enviar(); }}>
            {salvando === 'umidades' ? 'Gravando…' : 'Gravar cápsulas'}
          </button>
        </div>
      </div>
      <JanelaDeDescarte
        alvo={descartando ? `a determinação ${descartando.ordem} · ${descartando.capsula ?? ''}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar('descarte',
          () => descartarGranulometriaUmidade(id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </BlocoDaFicha>
  );
}

// ── 3 · as peneiras ───────────────────────────────────────────────────

function Peneiras({
  d, id, gravar, salvando, campoErro, equipamentos, fase, opcoesPeneira,
}: Comum & {
  campoErro(x: string): string | undefined;
  equipamentos: ReturnType<typeof useEquipamentos>;
  fase: 'grossa' | 'fina'; opcoesPeneira: Opcao[];
}) {
  const daFase = (d.peneiras ?? []).filter((p) => p.fase === fase);
  const serie = (d.serie ?? []).filter((s) => s.fase === fase);
  const gravada = (fase === 'grossa' ? d.balanca : d.balancaFina)?.id ?? '';
  const campoBalanca = fase === 'grossa'
    ? 'balancaEquipamentoId' : 'balancaFinaEquipamentoId';

  /*
   * O rascunho começa com a série inteira, e o que ficar em branco não
   * é enviado. É mais rápido do que escolher peneira a peneira num
   * seletor, e reproduz a folha de bancada, onde a coluna já vem impressa.
   */
  const [massas, setMassas] = useState<Record<string, string>>({});
  const [equipDe, setEquipDe] = useState<Record<string, string>>({});
  const [colagem, setColagem] = useState('');
  const [colando, setColando] = useState(false);
  const [balanca, setBalanca] = useState(gravada);
  useEffect(() => { setBalanca(gravada); }, [gravada]);

  useEffect(() => {
    const m: Record<string, string> = {};
    const e: Record<string, string> = {};
    for (const p of daFase) {
      m[String(p.abertura_mm)] = paraCampo(p.massa_retida);
      if (p.peneira_equipamento_id) {
        e[String(p.abertura_mm)] = p.peneira_equipamento_id;
      }
    }
    setMassas(m); setEquipDe(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.peneiras]);

  /**
   * A colagem: uma linha por peneira, `abertura<sep>massa`.
   *
   * Separadores: tabulação, ponto e vírgula, barra vertical. A vírgula
   * NÃO separa — em pt-BR ela é a decimal, e "0,85" viraria duas colunas.
   * Mesmo motivo e mesma regra do quadro de cápsulas dos triviais.
   */
  const aplicarColagem = () => {
    const linhas = colagem.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    const m = { ...massas };
    for (const t of linhas) {
      const p = t.split(/[\t;|]+/).map((s) => s.trim());
      if (p.length < 2) continue;
      const abertura = paraNumero(p[0]);
      const massa = paraNumero(p[1]);
      if (abertura == null || massa == null) continue;
      const chave = serie.find(
        (s) => Math.abs(Number(s.abertura_mm) - abertura) < 1e-6);
      if (!chave) continue;
      m[String(chave.abertura_mm)] = paraCampo(massa);
    }
    setMassas(m);
    setColagem('');
    setColando(false);
  };

  const lancadaDe = (abertura: unknown) =>
    daFase.find((p) => Number(p.abertura_mm) === Number(abertura));

  /* Só sobe o que mudou: reenviar a série inteira a cada gravação seria
     uma versão nova de cada peneira na auditoria sem nada ter mudado. */
  const mudadas = serie
    .map((s) => {
      const chave = String(s.abertura_mm);
      const p = lancadaDe(s.abertura_mm);
      const massa = paraNumero(massas[chave] ?? '');
      const eq = equipDe[chave] || undefined;
      const igual = p && massa != null
        && Math.abs(Number(p.massa_retida) - massa) < 1e-9
        && (p.peneira_equipamento_id ?? undefined) === eq;
      return igual || massa == null ? null : {
        fase,
        aberturaMm: Number(s.abertura_mm),
        massaRetida: massa,
        peneiraEquipamentoId: eq,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);
  const mudouBalanca = !!balanca && balanca !== gravada;
  const temMassa = daFase.length > 0 || mudadas.length > 0;

  const falta = temMassa && !balanca
    ? `Falta escolher a balança do peneiramento ${fase === 'grossa' ? 'grosso' : 'fino'}.`
    : mudadas.length || mudouBalanca ? null
      : daFase.length ? 'Nada mudou desde a última gravação.'
        : 'Lance a massa retida em pelo menos uma peneira para gravar.';

  const enviar = async () => {
    await gravar(`peneiras-${fase}`,
      () => gravarGranulometriaPeneiras(id, {
        ...(balanca && (mudouBalanca || mudadas.length)
          ? { [campoBalanca]: balanca } : {}),
        peneiras: mudadas,
      }));
  };

  /*
   * No celular cada peneira vira um cartão, com «passa (%)» no cabeçalho:
   * é o número que diz se a curva está andando, e ele não pode ficar no
   * fim de uma rolagem lateral.
   */
  const colunas: ColunaDeLancamento[] = [
    { texto: 'Peneira' },
    { texto: 'Abertura (mm)', num: true },
    { texto: 'Retida (g)', num: true },
    { texto: 'Retida acum. (g)', num: true },
    { texto: 'Passa (%)', num: true },
    { texto: 'Peneira usada' },
  ];

  const linhasDaSerie: LinhaDeLancamento[] = serie.map((s) => {
    const p = lancadaDe(s.abertura_mm);
    const chave = String(s.abertura_mm);
    return {
      chave,
      classe: p ? undefined : 'nova',
      titulo: (
        <>
          {s.designacao}
          {!s.da_norma && <> <Selo tom="neutro">Intermediária</Selo></>}
        </>
      ),
      resultado: p
        ? <span className="n">{pct(p.percentual_passa)} %</span> : undefined,
      /*
       * No cartão do celular sobram os dois campos em que se MEXE — a
       * massa retida e a peneira usada. A abertura já está no nome da
       * peneira, e a retida acumulada é leitura: vira nota, não campo.
       */
      nota: p ? `retida acumulada ${num(p.retido_acumulado, 2)} g` : undefined,
      semCartao: [0, 1, 3, 4],
      celulas: [
        <>
          {s.designacao}
          {!s.da_norma && <> <Selo tom="neutro">Intermediária</Selo></>}
        </>,
        num(s.abertura_mm, 3),
        <CampoNumerico
          rotulo={`Massa retida na ${s.designacao} (g)`} rotuloOculto
          value={massas[chave] ?? ''}
          onChange={(ev) => setMassas(
            (m) => ({ ...m, [chave]: ev.target.value }))}
        />,
        p ? num(p.retido_acumulado, 2) : '—',
        p ? <b className="fe-w">{pct(p.percentual_passa)}</b> : '—',
        <select value={equipDe[chave] ?? ''}
          aria-label={`Peneira usada na ${s.designacao}`}
          onChange={(ev) => setEquipDe(
            (m) => ({ ...m, [chave]: ev.target.value }))}>
          <option value="">—</option>
          {opcoesPeneira.map((o) => (
            <option key={o.valor} value={o.valor}>{o.texto}</option>
          ))}
        </select>,
      ],
    };
  });

  return (
    <BlocoDaFicha
      titulo={fase === 'grossa' ? 'Peneiramento grosso' : 'Peneiramento fino'}
      selo={daFase.length
        ? <Selo tom="ok">{plural(daFase.length, 'peneira', 'peneiras')}</Selo> : undefined}
      sub={fase === 'grossa'
        ? 'Massa retida em cada peneira, individual — não acumulada. A de 2,0 mm (nº 10) pertence a esta fase: é ela que define N.'
        : 'Massa retida individual do material passado na 2,0 mm. O divisor é Mu/(1+w), e o resultado é multiplicado por N.'}>
      <div className="fe-miolo fe-campos">
        <EscolhaDeBalanca
          rotulo={fase === 'grossa'
            ? 'Balança da amostra total e do grosso'
            : 'Balança da amostra parcial e do fino'}
          equipamentos={equipamentos} valor={balanca} aoEscolher={setBalanca}
          erro={campoErro(campoBalanca)} />
      </div>
      <div className="fe-tabela">
        <div className="fe-tabela-topo">
          <h3>Série {fase === 'grossa' ? 'grossa' : 'fina'} <small>{serie.length
            ? `${serie.length} peneiras` : 'série não cadastrada'}</small></h3>
          <button type="button" className="botao discreto pequeno"
            aria-pressed={colando} onClick={() => setColando((v) => !v)}>
            Colar da planilha
          </button>
        </div>
        {colando && (
          <div className="colagem">
            <textarea rows={3} value={colagem}
              aria-label={`Colar da planilha — peneiramento ${fase}`}
              placeholder={'Uma linha por peneira: abertura<TAB>massa retida\n'
                + (fase === 'grossa' ? '4,8\t12,40\n2\t5,37' : '0,6\t3,10\n0,075\t8,02')}
              onChange={(ev) => setColagem(ev.target.value)} />
            <button type="button" className="botao secundario"
              onClick={aplicarColagem} disabled={!colagem.trim()}>
              Ler a colagem
            </button>
          </div>
        )}
        <TabelaDeLancamento
          rotulo={`Peneiramento — fase ${fase}`}
          classe="fe-lanc peneiras"
          colunas={colunas}
          linhas={linhasDaSerie}
        />
        <div className="acoes acoes-fixas fe-acoes">
          {falta
            ? <p className="o-que-falta">{falta}</p>
            : <p className="fe-lembrete">Sobe só a peneira que mudou; a em branco fica de fora.</p>}
          <button type="button" className="principal" data-gravar-tabela=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={() => { if (!falta && salvando === null) void enviar(); }}>
            {salvando === `peneiras-${fase}` ? 'Gravando…'
              : `Gravar peneiramento ${fase === 'grossa' ? 'grosso' : 'fino'}`}
          </button>
        </div>
      </div>
    </BlocoDaFicha>
  );
}

// ── 4 · a sedimentação ────────────────────────────────────────────────

/** Os tempos de leitura da NBR 7181:2025, 4.3.9 — em segundos. */
export const TEMPOS_DA_NORMA = [
  30, 60, 120, 240, 480, 900, 1800, 3600, 7200, 14400, 28800, 86400,
];

export const rotuloTempo = (s: number) => (s < 60 ? `${s} s`
  : s < 3600 ? `${numero(s / 60, 0)} min` : `${numero(s / 3600, 0)} h`);

export const SITUACAO_LEITURA: Record<string, string> = {
  sem_densimetro: 'escolha o densímetro',
  densimetro_sem_curva: 'densímetro sem curva aprovada',
  sem_temperatura: 'sem temperatura',
  temperatura_fora_da_curva: 'temperatura fora da faixa calibrada',
  leitura_fora_da_curva: 'leitura fora da faixa calibrada',
  sem_volume_ou_area: 'densímetro sem volume/área',
  sem_massa_especifica: 'sem ρs',
  sem_massa_parcial: 'sem Mu',
};

function Sedimentacao({ d, id, gravar, salvando, opcoesDensimetro }: Comum & {
  opcoesDensimetro: Opcao[];
}) {
  const c = d.cabecalho;
  const lidas = d.sedimentacao ?? [];
  const [ligada, setLigada] = useState(false);
  const [densimetro, setDensimetro] = useState('');
  const [rhoInf, setRhoInf] = useState('');
  const [leitura, setLeitura] = useState<Record<number, string>>({});
  const [temp, setTemp] = useState<Record<number, string>>({});
  const [descartando, setDescartando] = useState<any | null>(null);

  useEffect(() => {
    setLigada(!!c?.com_sedimentacao);
    setDensimetro(c?.densimetro_equipamento_id ?? '');
    setRhoInf(c?.massa_especifica_informada != null
      ? paraCampo(c.massa_especifica_informada) : '');
    const l: Record<number, string> = {};
    const t: Record<number, string> = {};
    for (const x of lidas) {
      if (x.descartada) continue;
      l[x.tempo_s] = paraCampo(x.leitura);
      if (x.temperatura != null) t[x.tempo_s] = paraCampo(x.temperatura);
    }
    setLeitura(l); setTemp(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.com_sedimentacao, c?.densimetro_equipamento_id,
    c?.massa_especifica_informada, d.sedimentacao]);

  /* Os tempos da norma, mais qualquer outro já lançado. */
  const tempos = useMemo(() => Array.from(new Set([
    ...TEMPOS_DA_NORMA, ...lidas.map((x) => Number(x.tempo_s)),
  ])).sort((a, b) => a - b), [lidas]);

  const daLeitura = (t: number) =>
    lidas.find((x) => Number(x.tempo_s) === t && !x.descartada);

  const enviar = async () => {
    const leituras = tempos
      .map((t) => ({
        tempoS: t,
        leitura: paraNumero(leitura[t] ?? ''),
        temperatura: paraNumero(temp[t] ?? ''),
      }))
      .filter((x) => x.leitura != null)
      .map((x) => ({ ...x, leitura: x.leitura as number }));
    await gravar('sedimentacao', () => gravarGranulometriaSedimentacao(id, {
      comSedimentacao: ligada,
      densimetroEquipamentoId: densimetro || null,
      massaEspecificaInformada: paraNumero(rhoInf),
      leituras,
    }));
  };

  const colunas: ColunaDeLancamento[] = [
    { texto: 'Tempo' },
    { texto: 'Leitura L (g/cm³)', num: true },
    { texto: 'T (°C)', num: true },
    { texto: 'Lmd (g/cm³)', num: true },
    { texto: 'z (cm)', num: true },
    { texto: 'μ (10⁻⁶ g·s/cm²)', num: true },
    { texto: 'd (mm)', num: true },
    { texto: 'Passa (%)', num: true },
    { texto: '' },
  ];

  const linhas: LinhaDeLancamento[] = tempos.map((t, i) => {
    const x = daLeitura(t);
    const ok = x?.situacao === 'ok';
    return {
      chave: String(t),
      classe: x ? undefined : 'nova',
      titulo: rotuloTempo(t),
      resultado: ok
        ? <span className="n">{pct(x.percentual_passa)} %</span> : undefined,
      nota: x && ok ? `d = ${num(x.diametro_mm, 4)} mm · z = `
        + `${num(x.altura_queda, 2)} cm` : undefined,
      semCartao: [0, 3, 4, 5, 6, 7],
      celulas: [
        rotuloTempo(t),
        <CampoNumerico
          rotulo={`Leitura aos ${rotuloTempo(t)}`} rotuloOculto
          value={leitura[t] ?? ''}
          onChange={(ev) => setLeitura(
            (m) => ({ ...m, [t]: ev.target.value }))}
        />,
        <CampoNumerico
          rotulo={`Temperatura aos ${rotuloTempo(t)} (°C)`} rotuloOculto
          value={temp[t] ?? ''}
          placeholder={i < 2 ? 'medida depois' : undefined}
          onChange={(ev) => setTemp(
            (m) => ({ ...m, [t]: ev.target.value }))}
        />,
        x ? num(x.leitura_meio_dispersor, 4) : '—',
        x ? num(x.altura_queda, 2) : '—',
        x ? num(x.viscosidade, 3) : '—',
        x ? num(x.diametro_mm, 4) : '—',
        x ? (ok ? <b className="fe-w">{pct(x.percentual_passa)}</b>
          : <Selo tom="atencao">{SITUACAO_LEITURA[x.situacao] ?? x.situacao}</Selo>)
          : '—',
        x ? (
          <button type="button" className="botao discreto pequeno fe-descartar"
            aria-label={`Descartar a leitura dos ${rotuloTempo(t)}`}
            disabled={salvando !== null}
            onClick={() => setDescartando(x)}>
            Descartar
          </button>
        ) : null,
      ],
    };
  });

  const algo = tempos.some((t) => paraNumero(leitura[t] ?? '') != null);
  const falta = !ligada ? null
    : !densimetro ? 'Escolha o densímetro usado.'
      : !algo ? 'Lance pelo menos uma leitura.' : null;
  const origemRho = ({
    ensaio_mesp: 'do ensaio de massa específica da amostra',
    informada: 'transcrita',
    ausente: 'não determinada',
  } as Record<string, string>)[c?.massa_especifica_origem ?? 'ausente'];
  const primeira = lidas.find((x) => !x.descartada);

  return (
    <BlocoDaFicha titulo="Sedimentação" classe="sedimentacao"
      selo={ligada ? undefined : <Selo tom="neutro">Só peneiramento</Selo>}
      sub="ABNT NBR 7181:2025, 4.3 e 5.3–5.4. A leitura vira número pela curva do próprio densímetro (certificado interno aprovado, vigente na data do ensaio), interpolada entre os pontos medidos — fora da faixa medida não há número. A temperatura das duas primeiras leituras é a primeira medida depois delas (4.3.11).">
      <div className="fe-miolo fe-campos">
        <Escolha rotulo="Ensaio" valor={ligada ? 'sim' : 'nao'}
          aoEscolher={(v) => setLigada(v === 'sim')} limpavel={false}
          opcoes={[
            { valor: 'nao', texto: 'só peneiramento' },
            { valor: 'sim', texto: 'peneiramento e sedimentação' },
          ]} />
        {ligada && (
          <EscolhaPorCodigo
            rotulo="Densímetro"
            opcoes={opcoesDensimetro.map(comoCodigo)}
            valor={densimetro}
            aoEscolher={setDensimetro}
          />
        )}
        {ligada && (
          <Campo rotulo="ρs transcrita (g/cm³)" inputMode="decimal"
            value={rhoInf} onChange={(e) => setRhoInf(e.target.value)}
            dica="Só se a massa específica não foi determinada no sistema
                  (M.ESP da mesma amostra, que manda quando existe)." />
        )}
      </div>
      {ligada && (
        <>
          <div className="fe-miolo">
            <dl className="fe-mosaico">
              <div><dt>ρs</dt>
                <dd>{c?.massa_especifica_solidos == null ? '—'
                  : `${num(c.massa_especifica_solidos, 3)} g/cm³`}{' '}
                  <Selo tom={c?.massa_especifica_origem === 'ensaio_mesp'
                    ? 'ok' : 'atencao'}>{origemRho}</Selo></dd></div>
              <div><dt>ρmd a 20 °C</dt>
                <dd>{primeira?.massa_especifica_meio_dispersor == null ? '—'
                  : `${num(primeira.massa_especifica_meio_dispersor, 4)} g/cm³`}
                </dd></div>
              <div><dt>Ms — seca sedimentada</dt>
                <dd>{num(c?.massa_parcial_seca, 2)} g</dd></div>
              <div><dt>N — passa na 2,0 mm</dt>
                <dd>{pct(c?.percentual_passa_2mm)} %</dd></div>
            </dl>
          </div>
          <div className="fe-tabela">
            <div className="fe-tabela-topo">
              <h3>Leituras do densímetro <small>{plural(
                lidas.filter((x) => !x.descartada).length, 'lançada', 'lançadas')}</small></h3>
            </div>
            <TabelaDeLancamento
              rotulo="Leituras do densímetro"
              classe="fe-lanc sedimentacao"
              colunas={colunas}
              linhas={linhas}
            />
          </div>
        </>
      )}
      <div className="acoes acoes-fixas fe-acoes">
        {falta
          ? <p className="o-que-falta">{falta}</p>
          : <p className="fe-lembrete">Leitura errada se descarta com motivo — nada se apaga.</p>}
        <button type="button" className="principal" data-gravar-tabela=""
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? undefined}
          onClick={() => { if (!falta && salvando === null) void enviar(); }}>
          {salvando === 'sedimentacao' ? 'Gravando…' : 'Gravar sedimentação'}
        </button>
      </div>
      <JanelaDeDescarte
        alvo={descartando ? `a leitura dos ${rotuloTempo(Number(descartando.tempo_s))}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar('descarte',
          () => descartarGranulometriaLeitura(id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </BlocoDaFicha>
  );
}

// ── 5 · o resultado ───────────────────────────────────────────────────

export function Resultado({ c }: { c: any }) {
  const semSed = !c?.com_sedimentacao;
  return (
    <BlocoDaFicha titulo="Resultado" classe="resultado"
      sub="As frações pelas divisórias da ABNT NBR 6502; d10, d30 e d60 lidos na curva pelo banco (0048/0221) — a tela não recalcula nada.">
      <div className="fe-miolo">
        <dl className="fe-mosaico">
          <div><dt>Pedregulho (&gt; 2,0 mm)</dt>
            <dd>{pct(c?.fracao_pedregulho)} %</dd></div>
          <div><dt>Areia grossa (0,6 – 2,0 mm)</dt>
            <dd>{pct(c?.fracao_areia_grossa)} %</dd></div>
          <div><dt>Areia média (0,2 – 0,6 mm)</dt>
            <dd>{pct(c?.fracao_areia_media)} %</dd></div>
          {semSed ? (
            <div><dt>Passa na {num(c?.abertura_minima, 3)} mm</dt>
              <dd>{pct(c?.finos_abaixo_da_ultima_peneira)} %</dd></div>
          ) : (
            <>
              <div><dt>Areia fina (0,06 – 0,2 mm)</dt>
                <dd>{pct(c?.fracao_areia_fina)} %</dd></div>
              <div><dt>Silte (0,002 – 0,06 mm)</dt>
                <dd>{pct(c?.fracao_silte)} %</dd></div>
              <div><dt>Argila (&lt; 0,002 mm)</dt>
                <dd>{c?.fracao_argila == null
                  ? 'não alcançada' : `${pct(c.fracao_argila)} %`}</dd></div>
            </>
          )}
          <div className="destaque"><dt>D<sub>10</sub></dt>
            <dd>{c?.d10 == null ? '—' : `${num(c.d10, CASAS.d)} mm`}</dd></div>
          <div><dt>D<sub>30</sub></dt>
            <dd>{c?.d30 == null ? '—' : `${num(c.d30, CASAS.d)} mm`}</dd></div>
          <div><dt>D<sub>60</sub></dt>
            <dd>{c?.d60 == null ? '—' : `${num(c.d60, CASAS.d)} mm`}</dd></div>
          <div><dt>C<sub>u</sub></dt>
            <dd>{num(c?.coeficiente_uniformidade, CASAS.coef)}</dd></div>
          <div><dt>C<sub>c</sub></dt>
            <dd>{num(c?.coeficiente_curvatura, CASAS.coef)}</dd></div>
        </dl>
        {semSed && (
          <Aviso tipo="atencao" titulo="Silte e argila não foram determinados">
            Este ensaio foi feito só por peneiramento. As frações de silte e de
            argila exigem sedimentação (NBR 7181:2025, 4.3). O sistema não as
            estima: o último número verdadeiro da curva é o que passa na
            peneira mais fina usada.
          </Aviso>
        )}
      </div>
    </BlocoDaFicha>
  );
}

// ── a curva na tela ───────────────────────────────────────────────────

/**
 * A curva granulométrica — a mesma do laudo (onda 248).
 *
 * Os pontos são os do banco: abertura e percentual que passa de cada
 * peneira, e diâmetro e percentual de cada leitura de sedimentação com
 * situação «ok». Ligados em ordem de diâmetro, em log, como o laudo faz.
 * As divisórias da NBR 6502 só aparecem quando caem DENTRO da curva — a
 * mesma lição do laudo: legenda que manda procurar uma linha que não
 * existe é defeito.
 */
/** Os pontos da curva de uma leitura da ficha (peneiras e sedimentação). */
export function pontosDaCurvaGranulometrica(d: EnsaioGranulometria | null | undefined) {
  const pen = (d?.peneiras ?? [])
    .filter((p) => p.percentual_passa != null && Number(p.abertura_mm) > 0)
    .map((p) => ({ x: Number(p.abertura_mm), y: Number(p.percentual_passa) * 100 }));
  const sed = (d?.sedimentacao ?? [])
    .filter((l) => !l.descartada && l.situacao === 'ok'
      && l.diametro_mm != null && l.percentual_passa != null
      && Number(l.diametro_mm) > 0)
    .map((l) => ({ x: Number(l.diametro_mm), y: Number(l.percentual_passa) * 100 }));
  return { pen, sed, pts: [...pen, ...sed].sort((a, b) => a.x - b.x) };
}

/**
 * Os pontos em que a curva SOBE quando o diâmetro desce: o que passa numa
 * peneira não pode ser mais do que passa na peneira acima dela. A tela só
 * aponta (onda 323, a curva inicial da repetição); não corrige nada.
 */
export function pontosAnomalos(pts: { x: number; y: number }[]) {
  const ord = [...pts].sort((a, b) => a.x - b.x);
  return ord.filter((p, i) => i + 1 < ord.length && p.y > ord[i + 1].y + 1e-9);
}

export function CurvaGranulometrica({ d, inicial, titulo, acao }: {
  d: EnsaioGranulometria;
  /** Um controle no topo do bloco («mostrar a inicial»). */
  acao?: ReactNode;
  /**
   * A tentativa inicial, na repetição (onda 323): tracejada e vazada, sob a
   * atual — e os pontos ganham `data-serie` («inicial», «atual»).
   */
  inicial?: EnsaioGranulometria | null;
  titulo?: string;
}) {
  const c = d.cabecalho;
  const { pen, sed, pts } = pontosDaCurvaGranulometrica(d);
  const doInicial = inicial ? pontosDaCurvaGranulometrica(inicial).pts : [];
  const anomalos = new Set(pontosAnomalos(doInicial).map((p) => p.x));
  const nome = titulo ?? 'Curva granulométrica';
  const todos = [...pts, ...doInicial].sort((a, b) => a.x - b.x);

  if (todos.length < 2) {
    return (
      <div className="bloco fe-bloco fe-grafico">
        <header className="fe-bloco-topo"><h2><span>{nome}</span></h2>{acao}</header>
        <p className="sub">A curva aparece com duas peneiras lançadas.</p>
      </div>
    );
  }

  const xmin = todos[0].x;
  const xmax = todos[todos.length - 1].x;
  const janela = {
    x0: 10 ** Math.floor(Math.log10(xmin)),
    x1: 10 ** Math.ceil(Math.log10(xmax)),
    y0: 0, y1: 100,
  };
  const fronteiras = FRONTEIRAS.filter((f) => f.d > xmin && f.d < xmax);
  const achados = ([[10, c?.d10], [30, c?.d30], [60, c?.d60]] as [number, unknown][])
    .filter(([, v]) => v != null && Number(v) > 0)
    .map(([y, v]) => ({ y, x: Number(v) }));

  return (
    <div className="bloco fe-bloco fe-grafico">
      <header className="fe-bloco-topo">
        <h2><span>{nome}</span></h2>
        {acao ?? <Selo tom="neutro">A mesma do laudo</Selo>}
      </header>
      <div className="fe-miolo">
        <Plano janela={janela} escalaX="log"
          tituloX="Diâmetro dos grãos (mm)" tituloY="Porcentagem que passa (%)">
          {({ px }) => (
            <>
              {fronteiras.map((f) => {
                const a = px({ x: f.d, y: 0 });
                const b = px({ x: f.d, y: 100 });
                return <line key={f.d} className="fe-divisoria"
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
              })}
              {inicial && doInicial.length > 1 && (
                <Serie pontos={doInicial} px={px} marcar={false}
                  classe="repeticao-serie-inicial" curva="inicial" />
              )}
              {inicial && doInicial.map((p) => {
                const q = px(p);
                const anomalo = anomalos.has(p.x);
                return <circle key={`i${p.x}`} data-serie="inicial"
                  className={`ponto repeticao-ponto-inicial${anomalo ? ' repeticao-ponto-anomalo' : ''}`}
                  cx={q.x} cy={q.y} r={anomalo ? 6 : 4.5} />;
              })}
              {pts.length > 1 && (
                <Serie pontos={pts} px={px} marcar={false}
                  classe={inicial ? 'repeticao-serie-atual' : undefined}
                  curva={inicial ? 'atual' : undefined} />
              )}
              {pen.map((p) => {
                const q = px(p);
                return <circle key={`p${p.x}`} className="ponto"
                  data-serie={inicial ? 'atual' : undefined} cx={q.x} cy={q.y} r={4} />;
              })}
              {sed.map((p) => {
                const q = px(p);
                return <rect key={`s${p.x}`} className="ponto sed"
                  data-serie={inicial ? 'atual' : undefined}
                  x={q.x - 3.5} y={q.y - 3.5} width={7} height={7} />;
              })}
              {achados.map((a) => (
                <Achado key={a.y} p={a} px={px}
                  rotulo={`D${a.y} = ${num(a.x, CASAS.d)} mm`} />
              ))}
            </>
          )}
        </Plano>
        <p className="fe-legenda">
          {inicial && <span><i className="repeticao-l-inicial" />inicial (fora da conta)</span>}
          {inicial && <span><i className="l-aux" />curva inicial</span>}
          {inicial && anomalos.size > 0 && (
            <span><i className="repeticao-l-anomalo" />na inicial, passa mais que a peneira acima</span>
          )}
          {inicial && <span><i className="repeticao-l-atual" />repetição</span>}
          <span><i className={inicial ? 'repeticao-l-reta-atual' : 'l-reta'} />
            {inicial ? 'curva da repetição: ' : ''}peneiras{sed.length ? ' e sedimentação' : ''}</span>
          {sed.length > 0 && <span><i className="l-sed" />leitura do densímetro</span>}
          {fronteiras.length > 0
            ? <span><i className="l-aux" />divisórias da NBR 6502: {fronteiras
              .map((f) => `${num(f.d, f.d >= 1 ? 1 : f.d >= 0.01 ? 2 : 3)} mm`).join(' · ')}</span>
            : <span>nenhuma divisória da NBR 6502 dentro da curva</span>}
        </p>
      </div>
    </div>
  );
}
